import { useState, useCallback, useRef, useEffect } from "react";
import { useChatContext } from "../contexts/ChatContext";
import { useSourceContext } from "../contexts/SourcesContext";
import type { ProgressUpdate, StreamMessage } from "../types/api";
import type {
  ChatMessage,
  ChatMessageFeedback,
  ChatMode,
} from "../types/frontend";
import {
  MessageRoles,
  SourceTypeValues,
  ChatModeValues,
} from "../types/frontend";
import {
  sendMessageWithGroundedAcademicLiteratureResponse,
  sendMessageWithGroundedMateriomFormulationsResponse,
  sendMessageToAgent,
  submitFeedback,
} from "../api/chat";
import { processBuffer } from "../utils/streamProcessor";

/**
 * UseChatReturn interface defines the return type for the useChat hook.
 */
interface UseChatReturn {
  /** Indicates if the chat message is currently being processed. */
  isLoading: boolean;
  /** Stores any error message that occurred during the chat message processing. */
  error: string | null;
  /** Stores the progress of the chat message processing. */
  progress: ProgressUpdate | null;
  /**
   * Sends a chat message to the server and processes the response.
   * @param message The chat message to send.
   * @param chatHistory The history of chat messages in the current session.
   */
  sendMessage: (
    message: ChatMessage,
    chatHistory: ChatMessage[],
    searchSources?: boolean
  ) => Promise<void>;
  abortGeneration: () => void;
  sendFeedback: (
    traceId: string,
    payload: ChatMessageFeedback
  ) => Promise<void>;
}

/**
 * Options for the useChat hook
 */
interface UseChatOptions {
  /** Chat mode - "workflow" for grounded sources, "agent" for agentic endpoint */
  mode?: ChatMode;
}

/**
 * useChat hook provides functionality for sending and receiving chat messages.
 * @param options - Optional configuration including variant selection
 * @returns An object containing the isLoading state, error message, progress, and sendMessage function.
 */
export const useChat = (options: UseChatOptions = {}): UseChatReturn => {
  const { mode = ChatModeValues.Workflow } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addMessage, updateLastMessage, sessionId, setProgressUpdate } =
    useChatContext();

  // Always call useSourceContext (requires SourceProvider wrapper)
  // For v2, the values are available but not used
  const {
    addSources,
    sources: existingSources,
    sourceType,
    prependSources,
  } = useSourceContext();

  const abortControllerRef = useRef<AbortController | null>(null);
  const prevSessionIdRef = useRef(sessionId);

  // Abort any in-flight request when the session changes.
  // This prevents a response from Session A from leaking into Session B
  // when the user switches sessions while a response is still streaming.
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId]);

  const abortGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  /**
   * Sends a chat message to the server and processes the stream response.
   * @param message The chat message to send.
   * @param chatHistory The history of chat messages in the current session.
   */
  const sendMessage = useCallback(
    async (
      message: ChatMessage,
      chatHistory: ChatMessage[],
      searchSources?: boolean
    ) => {
      setIsLoading(true);
      setError(null);
      setProgressUpdate(null);

      abortControllerRef.current = new AbortController();

      try {
        // Choose the appropriate endpoint based on mode and sourceType
        let response: Response;
        if (mode === ChatModeValues.Agent) {
          // Agent mode uses the agent endpoint with streaming progress.
          // The inference-related parameters are set to match the defaults used in the chainlit integration.
          response = await sendMessageToAgent(
            {
              message: message.content,
              session_id: sessionId,
              deep_reasoning_enabled: true,
              n_researchers: 3,
            },
            abortControllerRef.current.signal
          );
        } else if (sourceType === SourceTypeValues.MateriomFormulation) {
          response = await sendMessageWithGroundedMateriomFormulationsResponse(
            message,
            chatHistory,
            sessionId,
            searchSources,
            abortControllerRef.current.signal
          );
        } else {
          // Default to scientific sources
          response = await sendMessageWithGroundedAcademicLiteratureResponse(
            message,
            chatHistory,
            sessionId,
            searchSources,
            abortControllerRef.current.signal
          );
        }

        const reader = response.body?.getReader();
        // Add initial empty message that will be updated with chunks,
        // with the same traceId as the user message

        if (!reader) {
          throw new Error("No response stream available");
        }

        // Add an initial assistant message to the chat
        addMessage({
          role: MessageRoles.Assistant,
          content: "",
          traceId: message.traceId,
          isProgress: true,
        });
        // Read the response from the chat API
        let fullResponse = "";
        let buffer = "";
        let isFirstChunk = true;
        // Read the stream response chunk by chunk
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          // Decode the chunk and add it to the buffer
          const chunk = new TextDecoder().decode(value);
          buffer += chunk;
          updateLastMessage(fullResponse);
          // Process the buffer to extract stream messages
          const result = processBuffer(
            buffer,
            fullResponse,
            isFirstChunk,
            updateLastMessage,
            setProgressUpdate,
            addSources,
            prependSources,
            existingSources
          );

          fullResponse = result.newContent;
          buffer = result.newBuffer;
          isFirstChunk = result.isFirstChunk;
        }

        // Process any remaining data in the buffer
        if (buffer.trim()) {
          try {
            const parsedChunk = JSON.parse(buffer) as StreamMessage;
            processBuffer(
              JSON.stringify(parsedChunk) + "\n", // Convert back to stream format
              fullResponse,
              isFirstChunk,
              updateLastMessage,
              setProgressUpdate,
              addSources,
              prependSources,
              existingSources
            );
          } catch (e) {
            console.warn("Failed to parse final buffer:", e);
          }
        }

        // Final update to mark message as complete
        updateLastMessage(fullResponse, { isProgress: false });
      } catch (err) {
        if (err instanceof Error) {
          if (err.name !== "AbortError") {
            setError(err.message);
            console.error("Chat error:", err);
          }
        } else {
          setError("An unexpected error occurred");
        }
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
        setProgressUpdate(null);
      }
    },
    [
      mode,
      addMessage,
      updateLastMessage,
      sessionId,
      setProgressUpdate,
      addSources,
      prependSources,
      existingSources,
      sourceType,
    ]
  );

  /**
   * Submits user feedback for a chat message to the backend.
   *
   * @param traceId - The unique identifier for the chat trace/message.
   * @param payload - The feedback payload containing score, reasons, and optional comment.
   *
   * This function wraps the submitFeedback API call and logs any errors encountered.
   */
  const sendFeedback = useCallback(
    async (traceId: string, payload: ChatMessageFeedback) => {
      try {
        // Attempt to send feedback to the backend API
        await submitFeedback(traceId, payload);
      } catch (err) {
        // Log any errors that occur during feedback submission
        console.error("Failed to send feedback:", err);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    progress: null, // Progress is handled via context
    sendMessage,
    abortGeneration,
    sendFeedback,
  };
};
